import { Link } from "react-router-dom";

/**
 * Explore Companies, filtered to one occupation.
 *
 * Shown when the page carries ?soc=<SOC code>. The data behind it is the Home
 * Office release of Certificates of Sponsorship by employer and occupation,
 * not our own employer list, so the rows deliberately carry only what that
 * release gives for EVERY employer: a name, how many they sponsored, and how
 * many other roles they sponsor. No logos and no descriptions, because those
 * exist for a few hundred companies out of sixty thousand and a half-filled
 * list looks broken.
 *
 * A redacted count is rendered as "1-5" rather than left blank. The Home
 * Office suppresses counts of five or fewer, so the absence of a number is
 * itself information: that employer did sponsor the role.
 */

const BLUE = "#1355ff";

const initials = (name) =>
  String(name || "")
    .replace(/[^A-Za-z ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "–";

const Monogram = ({ name, size }) => (
  <div
    aria-hidden="true"
    style={{
      width: size,
      height: size,
      borderRadius: size > 44 ? 14 : 10,
      background: BLUE,
      color: "#FFFFFF",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: size > 44 ? 15 : 13,
      fontWeight: 600,
      flex: "0 0 auto",
    }}
  >
    {initials(name)}
  </div>
);

const JobsBadge = ({ count }) =>
  count > 0 ? (
    <span
      style={{
        background: "#E9F6EE",
        color: "#15703C",
        fontSize: 12.5,
        fontWeight: 600,
        padding: "5px 11px",
        borderRadius: 8,
        whiteSpace: "nowrap",
      }}
    >
      {count} live {count === 1 ? "job" : "jobs"}
    </span>
  ) : null;

const rolesLine = (sponsor) =>
  sponsor.other_roles > 0
    ? `Also sponsors ${sponsor.other_roles} other ${
        sponsor.other_roles === 1 ? "role" : "roles"
      }`
    : "This role only";

const SponsorList = ({ sponsors, featured, occupation, total }) => {
  const occ = occupation || "this role";

  return (
    <div className="sponsor_occupation">
      {featured && featured.length > 0 && (
        <>
          <h3 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700 }}>
            Most active sponsors
          </h3>
          <p style={{ margin: "0 0 16px", fontSize: 14.5, color: "#5B6472" }}>
            The employers who sponsored {occ.toLowerCase()} most often in 2025.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(232px, 1fr))",
              gap: 16,
              marginBottom: 44,
            }}
          >
            {featured.map((f) => (
              <div
                key={`f-${f.org_name}`}
                style={{
                  background: "#FFFFFF",
                  borderRadius: 16,
                  padding: 20,
                  display: "flex",
                  flexDirection: "column",
                  boxShadow:
                    "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <Monogram name={f.org_name} size={40} />
                  <span style={{ marginLeft: "auto" }}>
                    <JobsBadge count={Number(f.live_jobs) || 0} />
                  </span>
                </div>
                <p
                  style={{
                    margin: "0 0 7px",
                    fontSize: 16.5,
                    fontWeight: 700,
                    letterSpacing: "-0.025em",
                    lineHeight: 1.3,
                  }}
                >
                  {f.org_name}
                </p>
                <p
                  style={{
                    margin: "0 0 18px",
                    fontSize: 13.5,
                    color: "#6B7482",
                  }}
                >
                  {rolesLine(f)}
                </p>
                <div
                  style={{
                    marginTop: "auto",
                    display: "flex",
                    alignItems: "baseline",
                    gap: 6,
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: BLUE,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {f.cos_label}
                  </span>
                  <span style={{ fontSize: 13, color: "#6B7482" }}>
                    sponsored in 2025
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
          {Number(total).toLocaleString()} employers sponsored {occ.toLowerCase()}
        </h3>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {sponsors.map((s) => (
          <div
            key={s.id}
            style={{
              background: "#FFFFFF",
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              alignItems: "center",
              padding: "14px 20px",
              borderRadius: 14,
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
          >
            <Monogram name={s.org_name} size={36} />

            <div style={{ flex: "1 1 240px", minWidth: 0 }}>
              <p
                style={{
                  margin: 0,
                  fontSize: 15.5,
                  fontWeight: 600,
                  letterSpacing: "-0.02em",
                }}
              >
                {s.org_name}
              </p>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: 13.5,
                  color: "#6B7482",
                }}
              >
                {rolesLine(s)}
              </p>
            </div>

            <JobsBadge count={Number(s.live_jobs) || 0} />

            <div style={{ flex: "0 0 110px", textAlign: "right" }}>
              <span
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {s.cos_label}
              </span>
              <span style={{ fontSize: 13, color: "#8B94A3" }}> sponsored</span>
            </div>
          </div>
        ))}
      </div>

      <p
        style={{
          margin: "28px 0 0",
          fontSize: 13,
          lineHeight: 1.7,
          color: "#8B94A3",
          maxWidth: 620,
        }}
      >
        Certificates of Sponsorship assigned January to December 2025, from Home
        Office data released under the Freedom of Information Act. Counts of
        five or fewer are published as a range. Recruitment agencies and
        employer-of-record providers are not listed.
      </p>
    </div>
  );
};

export default SponsorList;
